import React, { useCallback, useEffect, useState } from "react";
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
  CardBody,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { CardContent } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AddendumModal from "./AddendumModal";
import AddendumSendModal from "./AddendumSendModal";
import AddendumDocument from "../../../../../../components/Addendum/AddendumDocument";
import { SignatureCreator } from "../../../LeaseProcess/Flow/StepperComponents/lease/components/steps/components/SignatureService/SignatureCreator";
import { listAddendums, completeAddendum, voidAddendum, getAddendumSignedUrl } from "./api";
import { formatYmd } from "../../../../../../utils/dateOnly";

const getManagerName = () => {
  try {
    const u = JSON.parse(localStorage.getItem("authUser") || "{}");
    return u.name || u.displayName || u.first_name
      ? [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || u.displayName || "Property Manager"
      : "Property Manager";
  } catch {
    return "Property Manager";
  }
};

const STATUS_STYLE = {
  Draft: { bg: "#eef0f2", fg: "#5f5e5a", label: "Draft" },
  Sent: { bg: "#e6f1fb", fg: "#0c447c", label: "Awaiting signature" },
  PartiallySigned: { bg: "#faeeda", fg: "#854f0b", label: "Partially signed" },
  Signed: { bg: "#e1f5ee", fg: "#0f6e56", label: "Signed — ready to complete" },
  Completed: { bg: "#eaf3de", fg: "#27500a", label: "Completed" },
  Voided: { bg: "#fcebeb", fg: "#a32d2d", label: "Voided" },
};

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const summarize = (a) => {
  const parts = [];
  if (a.amendment_details?.parking?.selected) parts.push("Parking");
  if (a.amendment_details?.pet?.selected) parts.push(`Pet (${a.amendment_details.pet.description || "pet"})`);
  if ((a.amendment_details?.others || []).length) {
    parts.push(a.amendment_details.others.map((o) => o.label).filter(Boolean).join(" + ") || "Other");
  }
  return parts.join(" · ") || "Rent amendment";
};

const Badge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Draft;
  return (
    <span style={{ background: s.bg, color: s.fg, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
};

const AddendumsList = ({ itemId, item, tenants, propertyId, buildingName, propertyName, unitNo }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const res = await listAddendums(itemId);
      setItems(res.data?.data || []);
    } catch (e) {
      toast.error("Failed to load addendums");
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleComplete = async (a) => {
    const confirm = await Swal.fire({
      title: "Mark addendum completed?",
      text: "This applies the new rent/charges to the mastersheet. You can void it later to revert.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add my signature",
      confirmButtonColor: "#0f6e56",
    });
    if (!confirm.isConfirmed) return;
    setCompleteTarget(a);
  };

  const handleCompleteWithSig = async ({ signatureData }) => {
    const a = completeTarget;
    if (!a) return;
    setCompleteTarget(null);
    setBusyId(a._id);
    try {
      const landlord_signature = {
        name: getManagerName(),
        signature: signatureData || "",
      };
      const res = await completeAddendum(a._id, landlord_signature);
      toast.success(res.data?.message || "Addendum completed");
      if (previewTarget?._id === a._id) setPreviewTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to complete");
    } finally {
      setBusyId(null);
    }
  };

  const handleVoid = async (a) => {
    const confirm = await Swal.fire({
      title: "Void this addendum?",
      text: a.status === "Completed" ? "The mastersheet will be reverted to the values before this addendum." : "This addendum will be cancelled.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, void",
      confirmButtonColor: "#a32d2d",
    });
    if (!confirm.isConfirmed) return;
    setBusyId(a._id);
    try {
      await voidAddendum(a._id, confirm.value);
      toast.success("Addendum voided");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to void");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (a) => {
    setBusyId(a._id);
    try {
      const res = await getAddendumSignedUrl(a._id);
      const url = res.data?.data?.url;
      if (url) window.open(url, "_blank");
      else toast.error("No signed PDF available yet");
    } catch (e) {
      toast.error(e?.response?.data?.message || "No signed PDF available yet");
    } finally {
      setBusyId(null);
    }
  };

  const signedCount = (a) => (a.tenant_signatures || []).filter((t) => t.signed).length;

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <RButton color="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
          + Add addendum
        </RButton>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" /> <span className="text-muted ms-2">Loading…</span>
        </div>
      ) : items.length === 0 ? (
        <Alert color="info" className="my-2">
          No addendums yet. Click “Add addendum” to create one.
        </Alert>
      ) : (
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          {items.map((a) => {
            const busy = busyId === a._id;
            return (
              <div key={a._id} style={{ border: "1px solid #e6e9ee", borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <Badge status={a.status} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 600 }}>{summarize(a)}</div>
                  <small className="text-muted">
                    {a.addendum_id}
                    {a.effective_date ? ` · effective ${formatYmd(a.effective_date)}` : ""}
                    {` · ${signedCount(a)}/${(a.tenant_signatures || []).length} signed`}
                  </small>
                </div>
                <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{money(a.revised_rent?.new_total_monthly_rent)}</div>
                <div className="d-flex" style={{ gap: 6 }}>
                  {busy && <Spinner size="sm" />}
                  <RButton size="sm" color="secondary" outline onClick={() => setPreviewTarget(a)} disabled={busy}>
                    Preview
                  </RButton>
                  {a.status === "Draft" && (
                    <RButton size="sm" color="light" onClick={() => { setEditing(a); setModalOpen(true); }} disabled={busy}>
                      Edit
                    </RButton>
                  )}
                  {["Draft", "Sent", "PartiallySigned"].includes(a.status) && (
                    <RButton size="sm" color="info" outline onClick={() => setSendTarget(a)} disabled={busy}>
                      {a.status === "Draft" ? "Send" : "Resend"}
                    </RButton>
                  )}
                  {a.status === "Signed" && (
                    <RButton size="sm" color="success" onClick={() => handleComplete(a)} disabled={busy}>
                      Mark completed
                    </RButton>
                  )}
                  {a.status === "Completed" && (
                    <RButton size="sm" color="primary" outline onClick={() => handleDownload(a)} disabled={busy}>
                      View PDF
                    </RButton>
                  )}
                  {a.status !== "Voided" && (
                    <RButton size="sm" color="danger" outline onClick={() => handleVoid(a)} disabled={busy}>
                      Void
                    </RButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <AddendumModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          itemId={itemId}
          item={item}
          tenants={tenants}
          buildingName={buildingName}
          propertyName={propertyName}
          unitNo={unitNo}
          defaults={{ property_id: propertyId }}
          existing={editing}
          onSaved={load}
        />
      )}

      {sendTarget && (
        <AddendumSendModal
          isOpen={!!sendTarget}
          toggle={() => setSendTarget(null)}
          addendum={sendTarget}
          onSent={load}
        />
      )}

      <SignatureCreator
        isOpen={!!completeTarget}
        onClose={() => setCompleteTarget(null)}
        onSign={handleCompleteWithSig}
        disableSignAndSave={false}
      />

      <Modal isOpen={!!previewTarget} toggle={() => setPreviewTarget(null)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setPreviewTarget(null)}>Addendum preview</ModalHeader>
        <ModalBody style={{ background: "#f4f6f8" }}>
          {previewTarget && <AddendumDocument addendum={previewTarget} />}
        </ModalBody>
        <ModalFooter>
          {previewTarget?.status === "Signed" && (
            <RButton color="success" onClick={() => handleComplete(previewTarget)}>
              Approve &amp; mark completed
            </RButton>
          )}
          {previewTarget?.status === "Completed" && (
            <RButton color="primary" outline onClick={() => handleDownload(previewTarget)}>
              Download signed PDF
            </RButton>
          )}
          <RButton color="light" onClick={() => setPreviewTarget(null)}>
            Close
          </RButton>
        </ModalFooter>
      </Modal>
    </div>
  );
};

/**
 * Collapsible "Lease Addendums" section for the NewMasterSheet tenant modal —
 * mirrors the Lease History section (pill button + Collapse).
 */
const Addendums = ({ propertyId, buildingName, propertyName, unitNo, itemId, item, tenants }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton color="primary" outline className="fw-bold px-4 py-2 rounded-pill" onClick={() => setIsOpen((p) => !p)}>
          <FeatherIcon icon="file-text" className="me-2" size={16} />
          Lease Addendums
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          {!itemId ? (
            <Alert color="info" className="my-2">
              Save the tenant first to create lease addendums for this unit.
            </Alert>
          ) : (
            <AddendumsList
              itemId={itemId}
              item={item}
              tenants={tenants}
              propertyId={propertyId}
              buildingName={buildingName}
              propertyName={propertyName}
              unitNo={unitNo}
            />
          )}
        </CardContent>
      </RCollapse>
    </RCard>
  );
};

export { Addendums };
export default Addendums;
